import { Chess } from "@vendor/chess.js";
import { positionKey } from "../chess/positionKey.ts";
import type { OpeningId } from "../lessons/ids.ts";
import { isBlackOpening, loadedBlackCourse } from "../content/blackCourses.ts";
import { bookCoachingFor, MAINLINES, type BookCoachingNote } from "./bookCoaching.ts";
import { describeCandidate, positionGuideFor, structurePlanFor } from "./positionCoaching.ts";

export type ReviewedContinuation = {
  name: string;
  /** The note on this move was written for it, not generated from the board. */
  authored: boolean;
  note: BookCoachingNote;
};
type PendingContinuation = {
  /** The student's color in this course. */
  student: "w" | "b";
  moveNumber: number;
  san: string;
  option: NonNullable<BookCoachingNote["options"]>[number];
  index: number;
};
// Two sample lines can reach the same position by different move orders, so
// each position keeps every line through it; lookups prefer the line the
// student is following.
const continuations = new Map<string, PendingContinuation[]>();
const replies = new Map<string, { san: string; name: string }[]>();
const initialized = new Set<string>();

function add<T>(map: Map<string, T[]>, key: string, value: T) {
  map.set(key, [...(map.get(key) || []), value]);
}

/** The entry for the followed line, else the first line indexed here. */
function pick<T>(entries: T[] | undefined, nameOf: (entry: T) => string, follow?: string | null) {
  if (!entries?.length) return null;
  return (follow && entries.find((entry) => nameOf(entry) === follow)) || entries[0];
}

/** Index one course's sample lines the first time it is used (a Black course once loaded). */
function initialize(id: string | null | undefined) {
  if (!id || initialized.has(id)) return;
  const black = isBlackOpening(id) ? loadedBlackCourse(id) : null;
  if (isBlackOpening(id) && !black) return;
  const mainline = black ? black.mainline.split(" ") : MAINLINES[id as OpeningId];
  if (!mainline) return;
  initialized.add(id);
  const student = black ? ("b" as const) : ("w" as const);
  {
    const course = new Chess();
    for (let ply = 0; ply < mainline.length; ply += 1) {
      if (ply % 2 === (student === "w" ? 0 : 1)) {
        const lesson = bookCoachingFor(id, Math.floor(ply / 2) + 1);
        for (const option of lesson?.options || []) {
          const board = new Chess(course.fen());
          for (const [index, san] of (option.line || []).entries()) {
            const key = `${id}:${positionKey(board.fen())}`;
            const move = board.moves({ verbose: true }).find((candidate) => candidate.san === san);
            if (!move) break;
            if (index % 2 === 1) {
              add(replies, key, { san, name: option.name });
            } else if (index > 0) {
              const moveNumber = Math.floor((ply + index) / 2) + 1;
              add(continuations, key, { student, moveNumber, san, option, index });
            }
            board.move(san);
          }
        }
      }
      course.move(mainline[ply]);
    }
  }
}

/**
 * Exact positions only: a sample line is never replayed by move number.
 * With `san`, the continuation of any line through the position that plays
 * that move; otherwise the followed line's, else the first indexed.
 */
export function reviewedContinuationAtPosition(
  id: string | null | undefined,
  fen: string,
  san?: string | null,
  follow?: string | null
): ReviewedContinuation | null {
  initialize(id);
  const all = continuations.get(`${id}:${positionKey(fen)}`);
  const entry = pick(
    san ? all?.filter((candidate) => candidate.san === san) : all,
    (candidate) => candidate.option.name,
    follow
  );
  if (!entry) return null;
  const { student, moveNumber, option, index } = entry;
  const authored = option.continuation?.[index];
  const opponent = student === "w" ? "Black" : "White";
  const dots = student === "w" ? "..." : "";
  const board = new Chess(fen);
  const guide = positionGuideFor(id, moveNumber, board);
  const move = board.moves({ verbose: true }).find((candidate) => candidate.san === entry.san);
  if (!move) return null;
  // The why describes the sample move itself. The board guide's goal is left
  // out: it describes the position before this move, often the very threat
  // the move answers.
  const why =
    authored?.why || `${entry.san} ${describeCandidate(move, guide.priority, guide.threatened)}.`;
  // What the move sets up is read from the board after it, not from the
  // decision before it (which the move has just answered).
  const after = new Chess(fen);
  after.move(entry.san);
  const plan = structurePlanFor(id, after);
  const replied = option.line?.[index - 1];
  const following = option.line?.[index + 1];
  return {
    name: option.name,
    authored: Boolean(authored),
    note: {
      moveNumber,
      san: entry.san,
      goal: `Continue the sample line: ${option.name}.`,
      piece: guide.piece,
      candidates: `The illustrated continuation uses ${entry.san}. Other legal ideas to examine are ${
        guide.candidates
          .filter((candidate) => candidate.san !== entry.san)
          .slice(0, 3)
          .map((candidate) => candidate.san)
          .join(", ") || "other legal responses"
      }; calculate the replies before choosing.`,
      why,
      next: authored?.next || plan.next,
      watch: authored?.watch || guide.watch,
      depth: {
        strategy: `${option.why}${replied ? ` ${opponent} has replied ${dots}${replied}, as in the sample line.` : ""}`,
        tradeoff: option.watch || guide.watch,
        blackPlan: following
          ? `In the sample line, ${opponent} answers ${dots}${following}. That is one illustration; compare ${opponent}’s other checks, captures and central breaks.`
          : "",
        takeaway: `Keep the branch’s purpose, but choose the next move from the position ${opponent} actually gives you.`,
      },
    },
  };
}

export function reviewedReplyAtPosition(
  id: OpeningId | undefined,
  fen: string,
  follow?: string | null
): string | null {
  initialize(id);
  return pick(replies.get(`${id}:${positionKey(fen)}`), (entry) => entry.name, follow)?.san || null;
}
